// src/services/RealTime/WSClient.ts

type Options = {
  url: string;
  getToken: () => Promise<string>;
  debug?: boolean;
};

const DEFAULT_RECONNECT_MIN_DELAY_MS = 500
const DEFAULT_RECONNECT_MAX_DELAY_MS = 15000
const DEFAULT_RECONNECT_JITTER_RATIO = 0.2
const DEFAULT_CONNECT_TIMEOUT_MS = 10000
const DEFAULT_KEEP_ALIVE_INTERVAL_MS = 4 * 60 * 1000

function nowMs () {
  return Date.now()
}

function clamp (n, min, max) {
  return Math.max(min, Math.min(max, n))
}

function withJitter (ms, ratio) {
  const r = clamp(ratio, 0, 1)
  const delta = ms * r
  return Math.round(ms - delta + Math.random() * (delta * 2))
}

function safeJsonParse (text) {
  try {
    return JSON.parse(text)
  } catch (err) {
    return null
  }
}

function makeSubKey (topic, scope) {
  const t = topic == null ? '' : String(topic)
  const s = scope == null ? '' : String(scope)
  return `${t}::${s}`
}

function trLog (...args: any[]) {
  try {
    // eslint-disable-next-line no-console
    console.log('[Realtime][WSClient]', ...args)
  } catch (err) {
    // ignore
  }
}

export default class WSClient {
  _url = '';

  _getToken: any = null;
  _debug = false;

  _ws: any = null;
  _state: any = 'idle';
  _manualClose = false;

  _connectPromise: any = null;
  _connectResolve: any = null;
  _connectReject: any = null;
  _connectTimeoutTimer: any = null;

  _reconnectTimer: any = null;
  _reconnectAttempt = 0;

  _pendingSendQueue: any[] = [];

  _keepAliveTimer: any = null;
  _keepAliveIntervalMs = DEFAULT_KEEP_ALIVE_INTERVAL_MS;

  _desiredSubs: any = new Map();
  _handlersByKey: any = new Map();

  _onOpenExternal: any = null;
  _onCloseExternal: any = null;
  _onErrorExternal: any = null;
  _onStatusExternal: any = null;
  _onRawMessageExternal: any = null;
  _onReconnectedExternal: any = null;
  _onResubscribedExternal: any = null;

  constructor (options: Options) {
    let url = ''
    let getToken: any = null
    let debug = false

    const o = options || {}
    url = o && o.url ? String(o.url) : ''
    getToken = o && o.getToken ? o.getToken : null
    debug = Boolean(o && o.debug)

    this._url = url ? String(url) : ''
    this._getToken = getToken
    this._debug = debug

    this._ws = null
    this._state = 'idle'
    this._manualClose = false

    this._connectPromise = null
    this._connectResolve = null
    this._connectReject = null
    this._connectTimeoutTimer = null

    this._reconnectTimer = null
    this._reconnectAttempt = 0

    this._pendingSendQueue = []

    this._keepAliveTimer = null
    this._keepAliveIntervalMs = DEFAULT_KEEP_ALIVE_INTERVAL_MS

    this._desiredSubs = new Map()
    this._handlersByKey = new Map()

    this._onOpenExternal = null
    this._onCloseExternal = null
    this._onErrorExternal = null
    this._onStatusExternal = null
    this._onRawMessageExternal = null
    this._onReconnectedExternal = null
    this._onResubscribedExternal = null
  }

  setHandlers (handlers) {
    this._onOpenExternal = handlers && handlers.onOpen ? handlers.onOpen : null
    this._onCloseExternal =
      handlers && handlers.onClose ? handlers.onClose : null
    this._onErrorExternal =
      handlers && handlers.onError ? handlers.onError : null
    this._onStatusExternal =
      handlers && handlers.onStatus ? handlers.onStatus : null
    this._onRawMessageExternal =
      handlers && handlers.onRawMessage ? handlers.onRawMessage : null
    this._onReconnectedExternal =
      handlers && handlers.onReconnected ? handlers.onReconnected : null
    this._onResubscribedExternal =
      handlers && handlers.onResubscribed ? handlers.onResubscribed : null
  }

  getState () {
    return this._state
  }

  isConnected () {
    return this._ws != null && this._ws.readyState === WebSocket.OPEN
  }

  connect () {
    if (this.isConnected()) {
      return Promise.resolve()
    }

    if (this._connectPromise) {
      return this._connectPromise
    }

    this._manualClose = false
    this._clearReconnectTimer()

    this._state = 'connecting'
    this._emitStatus()

    this._connectPromise = new Promise((resolve, reject) => {
      this._connectResolve = resolve
      this._connectReject = reject

      Promise.resolve(this._getToken ? this._getToken() : null)
        .then((token) => {
          const url = this._resolveUrl(token)
          if (!url) {
            this._finishConnectReject(new Error('WSClient: Missing url'))
            return
          }

          let ws
          try {
            ws = new WebSocket(url)
          } catch (err) {
            this._finishConnectReject(err)
            return
          }

          this._ws = ws

          this._clearConnectTimeout()
          this._connectTimeoutTimer = setTimeout(() => {
            this._connectTimeoutTimer = null

            // If connect is still in progress, force-close and retry.
            if (this._state !== 'open' && !this._manualClose) {
              try {
                ws.close()
              } catch (err) {
                // ignore
              }

              this._ws = null
              this._state = 'closed'
              this._emitStatus({ reason: 'connect_timeout' })

              this._finishConnectReject(new Error('WSClient: connect timeout'))
              this._scheduleReconnect()
            }
          }, DEFAULT_CONNECT_TIMEOUT_MS)

          ws.onopen = () => {
            trLog('ws open')
            this._startKeepAlive()
            this._clearConnectTimeout()
            this._state = 'open'
            const reconnectAttempt = this._reconnectAttempt
            const wasReconnect = reconnectAttempt > 0
            this._reconnectAttempt = 0
            this._emitStatus()

            this._flushPendingSendQueue()
            this._resubscribeAll()

            if (wasReconnect && this._onReconnectedExternal) {
              this._onReconnectedExternal({ attempt: reconnectAttempt })
            }

            if (wasReconnect && this._onResubscribedExternal) {
              this._onResubscribedExternal({
                attempt: reconnectAttempt,
                count: this._desiredSubs.size
              })
            }

            if (this._onOpenExternal) {
              this._onOpenExternal()
            }

            this._finishConnectResolve()
          }

          ws.onmessage = (evt) => {
            trLog('ws message received')
            const raw = evt && evt.data != null ? evt.data : null
            this._handleRawMessage(raw)
          }

          ws.onerror = (evt) => {
            trLog('ws error', evt)
            if (this._onErrorExternal) {
              this._onErrorExternal(evt)
            }
          }

          ws.onclose = (evt) => {
            trLog('ws close', evt && (evt.code != null ? evt.code : null), evt && (evt.reason != null ? evt.reason : null))
            this._stopKeepAlive()
            this._clearConnectTimeout()
            const wasManual = this._manualClose

            this._ws = null
            this._state = wasManual ? 'closed' : 'closed'
            this._emitStatus()

            if (this._onCloseExternal) {
              this._onCloseExternal(evt)
            }

            if (!wasManual) {
              this._scheduleReconnect()
            }
          }
        })
        .catch((err) => {
          this._finishConnectReject(err)
        })
    })

    return this._connectPromise
  }

  disconnect () {
    this._manualClose = true
    this._clearReconnectTimer()
    this._clearConnectTimeout()
    this._stopKeepAlive()

    if (this._ws) {
      try {
        this._ws.close()
      } catch (err) {
        // ignore
      }
    }

    this._ws = null
    this._state = 'closed'
    this._emitStatus()

    this._finishConnectReject(new Error('WSClient: disconnected'))
  }

  send (data) {
    const payload = this._normalizeOutgoing(data)

    if (this.isConnected()) {
      try {
        this._ws.send(payload)
        return true
      } catch (err) {
        return false
      }
    }

    this._pendingSendQueue.push(payload)
    return false
  }

  subscribe (topic, scope, handler) {
    const key = makeSubKey(topic, scope)

    if (!this._desiredSubs.has(key)) {
      this._desiredSubs.set(key, { topic, scope })
    }

    if (handler) {
      const arr = this._handlersByKey.get(key) || []
      arr.push(handler)
      this._handlersByKey.set(key, arr)
    }

    if (this.isConnected()) {
      this._sendSubscribe(topic, scope)
    }

    return () => {
      this.unsubscribe(topic, scope, handler)
    }
  }

  unsubscribe (topic, scope, handler) {
    const key = makeSubKey(topic, scope)

    if (handler) {
      const arr = this._handlersByKey.get(key) || []
      const next = arr.filter((h) => h !== handler)
      if (next.length > 0) {
        this._handlersByKey.set(key, next)
      } else {
        this._handlersByKey.delete(key)
      }
    }

    const stillHasHandlers = this._handlersByKey.has(key)

    if (!stillHasHandlers) {
      this._desiredSubs.delete(key)
      if (this.isConnected()) {
        this._sendUnsubscribe(topic, scope)
      }
    }
  }

  clearSubscriptions () {
    const keys = Array.from(this._desiredSubs.keys())
    for (const key of keys) {
      const item = this._desiredSubs.get(key)
      if (item && this.isConnected()) {
        this._sendUnsubscribe(item.topic, item.scope)
      }
    }

    this._desiredSubs.clear()
    this._handlersByKey.clear()
  }

  _resolveUrl (token: any) {
    if (!this._url) {
      return ''
    }

    const t = token == null ? '' : String(token)
    if (!t) {
      return this._url
    }

    if (this._url.indexOf('?') >= 0) {
      return `${this._url}&token=${encodeURIComponent(t)}`
    }

    return `${this._url}?token=${encodeURIComponent(t)}`
  }

  _normalizeOutgoing (data) {
    if (data == null) {
      return ''
    }

    if (typeof data === 'string') {
      return data
    }

    try {
      return JSON.stringify(data)
    } catch (err) {
      return String(data)
    }
  }

  _handleRawMessage (raw) {
    const text = raw == null ? '' : String(raw)

    if (this._onRawMessageExternal) {
      // Always pass a string so consumers don't have to handle Blob/ArrayBuffer.
      this._onRawMessageExternal(text)
    }

    trLog('raw', text)

    const obj = safeJsonParse(text)

    if (obj == null) {
      this._dispatchToHandlers(null, null, text, null)
      return
    }

    const topic = obj.topic != null ? obj.topic : obj.channel

    // Payload may include multiple scopes. This is metadata; a single incoming WS message
    // must result in a single dispatch if ANY subscribed scope matches.
    const scopes = Array.isArray((obj as any).scopes) ? (obj as any).scopes : null
    if (scopes && scopes.length > 0) {
      for (const s of scopes) {
        const scope = s != null ? String(s) : null
        const key = makeSubKey(topic, scope)
        const handlers = this._handlersByKey.get(key) || []
        if (handlers.length > 0) {
          this._dispatchToHandlers(topic, scope, text, obj)
          return
        }
      }

      // No matching per-scope handler; fall back to wildcard handlers if any.
      this._dispatchToHandlers(topic, null, text, obj)
      return
    }

    const scope = obj.scope != null ? obj.scope : obj.group

    this._dispatchToHandlers(topic, scope, text, obj)
  }

  _dispatchToHandlers (topic, scope, rawText, obj) {
    const key = makeSubKey(topic, scope)
    const handlers = this._handlersByKey.get(key) || []
    trLog('dispatch', { topic, scope, hasHandlers: handlers.length > 0 })

    if (handlers.length === 0) {
      const anyHandlers = this._handlersByKey.get(makeSubKey('*', '*')) || []
      for (const h of anyHandlers) {
        try {
          h({ topic, scope, raw: rawText, data: obj })
        } catch (err) {
          // ignore
        }
      }
      return
    }

    for (const h of handlers) {
      try {
        h({ topic, scope, raw: rawText, data: obj })
      } catch (err) {
        // ignore
      }
    }
  }

  _sendSubscribe (topic, scope) {
    this.send({ op: 'subscribe', topic, scope })
  }

  _sendUnsubscribe (topic, scope) {
    this.send({ op: 'unsubscribe', topic, scope })
  }

  _resubscribeAll () {
    const subs = Array.from(this._desiredSubs.values()) as any[]
    for (const item of subs) {
      const it = item as any
      this._sendSubscribe(it.topic, it.scope)
    }
  }

  _flushPendingSendQueue () {
    if (!this.isConnected()) {
      return
    }

    const queue = this._pendingSendQueue.slice(0)
    this._pendingSendQueue.length = 0

    for (const payload of queue) {
      try {
        this._ws.send(payload)
      } catch (err) {
        // ignore
      }
    }
  }

  _scheduleReconnect () {
    if (this._manualClose) {
      return
    }

    this._clearReconnectTimer()

    this._reconnectAttempt += 1

    const base =
      DEFAULT_RECONNECT_MIN_DELAY_MS * Math.pow(2, this._reconnectAttempt - 1)
    const capped = clamp(
      base,
      DEFAULT_RECONNECT_MIN_DELAY_MS,
      DEFAULT_RECONNECT_MAX_DELAY_MS
    )
    const delay = withJitter(capped, DEFAULT_RECONNECT_JITTER_RATIO)

    this._state = 'reconnecting'
    this._emitStatus({ attempt: this._reconnectAttempt, delayMs: delay })

    this._reconnectTimer = setTimeout(() => {
      this._reconnectTimer = null
      this.connect().catch(() => {
        // If connect fails before a WebSocket exists (token error, constructor error),
        // there will be no onclose event to trigger another reconnect.
        if (this._manualClose) {
          return
        }
        this._scheduleReconnect()
      })
    }, delay)
  }

  _clearReconnectTimer () {
    if (this._reconnectTimer) {
      clearTimeout(this._reconnectTimer)
      this._reconnectTimer = null
    }
  }

  _clearConnectTimeout () {
    if (this._connectTimeoutTimer) {
      clearTimeout(this._connectTimeoutTimer)
      this._connectTimeoutTimer = null
    }
  }

  _startKeepAlive () {
    this._stopKeepAlive()

    if (this._manualClose) {
      return
    }

    this._keepAliveTimer = setInterval(() => {
      if (this._manualClose) {
        return
      }

      if (!this.isConnected()) {
        return
      }

      try {
        this.send({ op: 'ping' })
        trLog('ws ping')
      } catch (err) {
        // ignore
      }
    }, this._keepAliveIntervalMs)
  }

  _stopKeepAlive () {
    if (this._keepAliveTimer) {
      clearInterval(this._keepAliveTimer)
      this._keepAliveTimer = null
    }
  }

  _emitStatus (extra: any = null) {
    if (!this._onStatusExternal) {
      return
    }

    try {
      this._onStatusExternal({
        state: this._state,
        attempt: this._reconnectAttempt,
        connected: this.isConnected(),
        time: nowMs(),
        extra: extra || null
      })
    } catch (err) {
      // ignore
    }
  }

  _finishConnectResolve () {
    this._clearConnectTimeout()
    const resolve = this._connectResolve
    this._connectResolve = null
    this._connectReject = null
    this._connectPromise = null

    if (resolve) {
      resolve()
    }
  }

  _finishConnectReject (err) {
    this._clearConnectTimeout()
    const reject = this._connectReject
    this._connectResolve = null
    this._connectReject = null
    this._connectPromise = null

    if (reject) {
      reject(err)
    }
  }
}
